import React, { useState, useRef, useEffect } from 'react';
import {
  LayoutGrid,
  FileText,
  Grid3x3,
  Maximize2,
  X,
  Plus,
  Layers,
} from 'lucide-react';
import { DawViewMode, SplitLayoutConfig } from '../types/midi';

interface DawSplitWorkspaceProps {
  layoutConfig: SplitLayoutConfig;
  onLayoutConfigChange: (config: SplitLayoutConfig) => void;
  renderPianoRoll: () => React.ReactNode;
  renderScore: () => React.ReactNode;
  renderChart: () => React.ReactNode;
  showVirtualKeyboard?: boolean;
  onToggleVirtualKeyboard?: () => void;
  showVelocityLane?: boolean;
  onToggleVelocityLane?: () => void;
}

const VIEW_METADATA: Record<
  DawViewMode,
  { label: string; shortLabel: string; icon: React.FC<{ className?: string }>; color: string }
> = {
  pianoroll: {
    label: 'Piano Roll & Velocity',
    shortLabel: 'Piano Roll',
    icon: LayoutGrid,
    color: 'text-indigo-400',
  },
  score: {
    label: 'Score (Grand Staff)',
    shortLabel: 'Score',
    icon: FileText,
    color: 'text-amber-400',
  },
  chart: {
    label: 'Chord Chart (Grid)',
    shortLabel: 'Chart',
    icon: Grid3x3,
    color: 'text-emerald-400',
  },
};

export const DawSplitWorkspace: React.FC<DawSplitWorkspaceProps> = ({
  layoutConfig,
  onLayoutConfigChange,
  renderPianoRoll,
  renderScore,
  renderChart,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Active pane views (1, 2, or 3)
  const panes = layoutConfig.panes.slice(0, layoutConfig.paneCount);
  const direction = layoutConfig.direction;

  // Split ratios in percentages
  const defaultRatios =
    panes.length === 1
      ? [100]
      : panes.length === 2
      ? [50, 50]
      : [33.33, 33.33, 33.34];

  const ratios = layoutConfig.splitRatios && layoutConfig.splitRatios.length === panes.length
    ? layoutConfig.splitRatios
    : defaultRatios;

  // Dragging state for splitter handles
  const [resizingSplitterIndex, setResizingSplitterIndex] = useState<number | null>(null);
  const dragStartPosRef = useRef<number>(0);
  const initialRatiosRef = useRef<number[]>([]);

  const handleSplitterMouseDown = (index: number, e: React.MouseEvent) => {
    e.preventDefault();
    setResizingSplitterIndex(index);
    dragStartPosRef.current = direction === 'horizontal' ? e.clientY : e.clientX;
    initialRatiosRef.current = [...ratios];
  };

  useEffect(() => {
    if (resizingSplitterIndex === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current || resizingSplitterIndex === null) return;
      const rect = containerRef.current.getBoundingClientRect();
      const totalSize = direction === 'horizontal' ? rect.height : rect.width;
      if (totalSize <= 0) return;

      const currentPos = direction === 'horizontal' ? e.clientY : e.clientX;
      const deltaPixels = currentPos - dragStartPosRef.current;
      const deltaPercent = (deltaPixels / totalSize) * 100;

      const newRatios = [...initialRatiosRef.current];
      const minPercent = 18; // Minimum 18% width/height for any pane

      const leftPane = resizingSplitterIndex;
      const rightPane = resizingSplitterIndex + 1;

      let newLeft = initialRatiosRef.current[leftPane] + deltaPercent;
      let newRight = initialRatiosRef.current[rightPane] - deltaPercent;

      if (newLeft < minPercent) {
        newRight -= minPercent - newLeft;
        newLeft = minPercent;
      }
      if (newRight < minPercent) {
        newLeft -= minPercent - newRight;
        newRight = minPercent;
      }

      newRatios[leftPane] = Math.max(minPercent, newLeft);
      newRatios[rightPane] = Math.max(minPercent, newRight);

      onLayoutConfigChange({
        ...layoutConfig,
        splitRatios: newRatios,
      });
    };

    const handleMouseUp = () => {
      setResizingSplitterIndex(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingSplitterIndex, direction, layoutConfig, onLayoutConfigChange]);

  const handleResetSplit = () => {
    const count = panes.length;
    const equal = count === 1 ? [100] : count === 2 ? [50, 50] : [33.33, 33.33, 33.34];
    onLayoutConfigChange({
      ...layoutConfig,
      splitRatios: equal,
    });
  };

  const handleSetPaneView = (paneIndex: number, newView: DawViewMode) => {
    const nextPanes = [...layoutConfig.panes];
    nextPanes[paneIndex] = newView;
    onLayoutConfigChange({
      ...layoutConfig,
      panes: nextPanes,
    });
  };

  const handleMaximizePane = (paneIndex: number) => {
    const selectedView = panes[paneIndex] || 'pianoroll';
    onLayoutConfigChange({
      ...layoutConfig,
      paneCount: 1,
      panes: [selectedView, ...layoutConfig.panes.filter((v) => v !== selectedView)],
      splitRatios: [100],
    });
  };

  const handleClosePane = (paneIndex: number) => {
    if (layoutConfig.paneCount <= 1) return;
    const nextPanes = panes.filter((_, idx) => idx !== paneIndex);
    const newCount = (layoutConfig.paneCount - 1) as 1 | 2;
    const newRatios = newCount === 1 ? [100] : [50, 50];
    onLayoutConfigChange({
      ...layoutConfig,
      paneCount: newCount,
      panes: nextPanes,
      splitRatios: newRatios,
    });
  };

  const handleAddPane = () => {
    if (layoutConfig.paneCount >= 3) return;
    const allViews: DawViewMode[] = ['pianoroll', 'score', 'chart'];
    const missingView = allViews.find((v) => !panes.includes(v)) || 'score';
    const nextPanes = [...panes, missingView];
    const newCount = (layoutConfig.paneCount + 1) as 2 | 3;
    const newRatios = newCount === 2 ? [50, 50] : [33.33, 33.33, 33.34];
    onLayoutConfigChange({
      ...layoutConfig,
      paneCount: newCount,
      direction: 'vertical',
      panes: nextPanes,
      splitRatios: newRatios,
    });
  };

  const renderViewContent = (view: DawViewMode) => {
    switch (view) {
      case 'pianoroll':
        return renderPianoRoll();
      case 'score':
        return renderScore();
      case 'chart':
        return renderChart();
      default:
        return renderPianoRoll();
    }
  };

  return (
    <div
      ref={containerRef}
      id="daw-split-workspace"
      className={`flex-1 flex ${
        direction === 'horizontal' ? 'flex-col' : 'flex-col md:flex-row'
      } min-h-0 min-w-0 bg-[#141210] overflow-hidden select-none relative`}
    >
      {panes.map((paneView, index) => {
        const ratio = ratios[index] ?? (100 / panes.length);
        const isLast = index === panes.length - 1;

        const paneStyle: React.CSSProperties = {
          flex: `${ratio} ${ratio} 0%`,
          minWidth: direction === 'vertical' && panes.length > 1 ? '240px' : undefined,
          minHeight: direction === 'horizontal' && panes.length > 1 ? '160px' : undefined,
        };

        return (
          <React.Fragment key={`pane-${index}-${paneView}`}>
            <div
              id={`daw-split-pane-${index}`}
              style={paneStyle}
              className="flex flex-col min-h-0 min-w-0 overflow-hidden bg-[#161412] border border-[#2D2820] relative group"
            >
              {/* Zone 3: Workspace Viewport Bar (Sélecteur d'éditeurs & Espace de travail) */}
              <div
                id={`pane-header-${index}`}
                className="h-8 sm:h-9 px-2.5 sm:px-3 bg-gradient-to-r from-[#14121e] via-[#161424] to-[#12111b] border-b border-indigo-950/70 flex items-center justify-between text-xs text-[#EBE6DB] shrink-0 z-10 select-none shadow-[inset_0_1px_0_rgba(129,140,248,0.08)]"
              >
                {/* Left: Viewport Mode Tag & View selector tabs */}
                <div className="flex items-center gap-2 sm:gap-2.5">
                  {/* Distinct Viewport Indicator */}
                  <div className="hidden xs:flex items-center gap-1.5 text-[9px] uppercase tracking-wider font-bold text-indigo-200 bg-[#0e0c17] px-2 py-0.5 border border-indigo-800/60 shadow-xs rounded-xs">
                    <Layers className="w-3 h-3 text-indigo-400" />
                    <span className="font-mono text-indigo-300">Workspace</span>
                  </div>

                  <div
                    id={`pane-tabs-${index}`}
                    className="flex items-center bg-[#0d0c14] p-0.5 border border-indigo-900/50 gap-1 shadow-xs rounded-xs"
                  >
                    {(['pianoroll', 'score', 'chart'] as DawViewMode[]).map((v) => {
                      const optMeta = VIEW_METADATA[v];
                      const OptIcon = optMeta.icon;
                      const isCurrent = paneView === v;

                      const activeClasses =
                        'bg-indigo-600 text-white font-bold border-indigo-400 shadow-[0_0_12px_rgba(99,102,241,0.4)]';

                      return (
                        <button
                          key={v}
                          id={`pane-${index}-tab-${v}`}
                          type="button"
                          onClick={() => handleSetPaneView(index, v)}
                          className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1 text-xs transition-all border rounded-xs ${
                            isCurrent
                              ? activeClasses
                              : 'text-indigo-200/70 hover:text-white hover:bg-indigo-950/60 border-transparent font-medium'
                          }`}
                          title={`Show ${optMeta.label} in this pane`}
                        >
                          <OptIcon
                            className={`w-3.5 h-3.5 transition-colors ${
                              isCurrent ? 'text-white' : 'text-indigo-300/80'
                            }`}
                          />
                          <span className="leading-none whitespace-nowrap">{optMeta.shortLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Right: Pane Actions */}
                <div className="flex items-center gap-1.5">
                  {layoutConfig.paneCount > 1 && (
                    <span className="hidden sm:inline-block text-[10px] text-indigo-300/80 font-mono bg-[#0f0e18] px-1.5 py-0.5 border border-indigo-900/60 rounded-xs">
                      {Math.round(ratio)}%
                    </span>
                  )}

                  {/* Maximize / Solo view button */}
                  {layoutConfig.paneCount > 1 && (
                    <button
                      id={`pane-${index}-maximize-btn`}
                      type="button"
                      onClick={() => handleMaximizePane(index)}
                      className="p-1.5 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors border border-transparent rounded"
                      title="Maximize this pane (Single view)"
                    >
                      <Maximize2 className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Add pane button (if < 3) */}
                  {layoutConfig.paneCount < 3 && index === panes.length - 1 && (
                    <button
                      id={`pane-${index}-add-btn`}
                      type="button"
                      onClick={handleAddPane}
                      className="p-1.5 text-[#A89F91] hover:text-[#F5C767] hover:bg-[#25211B] transition-colors border border-transparent rounded"
                      title="Add split pane (+)"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* Close pane button (if > 1) */}
                  {layoutConfig.paneCount > 1 && (
                    <button
                      id={`pane-${index}-close-btn`}
                      type="button"
                      onClick={() => handleClosePane(index)}
                      className="p-1.5 text-[#A89F91] hover:text-red-400 hover:bg-red-950/40 transition-colors border border-transparent rounded"
                      title="Close pane"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Pane Content Area with guaranteed min-h-0 and no clipping */}
              <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden relative">
                {renderViewContent(paneView)}
              </div>
            </div>

            {/* Splitter Handle between panes */}
            {!isLast && (
              <div
                id={`daw-splitter-${index}`}
                onMouseDown={(e) => handleSplitterMouseDown(index, e)}
                onDoubleClick={handleResetSplit}
                title="Drag to resize • Double-click to reset (50/50)"
                className={`${
                  direction === 'horizontal'
                    ? 'h-2 w-full cursor-row-resize flex items-center justify-center hover:bg-[#F5C767]/20 active:bg-[#F5C767]/30'
                    : 'w-2 h-full cursor-col-resize flex items-center justify-center hover:bg-[#F5C767]/20 active:bg-[#F5C767]/30'
                } bg-[#1C1A17] border-[#2D2820] shrink-0 select-none transition-colors z-20 group/splitter`}
              >
                <div
                  className={`${
                    direction === 'horizontal' ? 'w-8 h-1' : 'w-1 h-8'
                  } bg-[#3D362C] group-hover/splitter:bg-[#F5C767] transition-colors`}
                />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
